/* ============================================================
   PUNGAO — AWTOMIC PORTAL ENHANCEMENTS (v38)
   File: assets/awtomic-portal.js

   Loaded from theme.liquid (before </body>):
   <script src="{{ 'awtomic-portal.js' | asset_url }}" defer></script>

   v38 CHANGES
   - Flavor pills on mobile: fixed 2-column grid instead of
     stretch-wrap, so a lone third flavor keeps the same width
     as the others (no accidental emphasis) and leaves an empty
     slot for a future 4th flavor.
   - Extras row label shortened to "Add extras to next order"
     so it stays on one line on mobile.
   - (CSS v32 trims the mobile CHANGE FLAVOR button height to
     match the pack-size dropdown.)

   v37 CHANGES — CHANGE FLAVOR (v2)
   - Flavor-capable products (any option whose name contains
     "Flavor": Vital, Kinesis, The Bundle) lose their inline
     flavor dropdowns and gain a compact neon-yellow CHANGE
     FLAVOR button in their place (dropdown-height; CSS v31).
     Pack size + Update + Swap stay inline. Non-flavor products
     (Apiary/merch) untouched.
   - Modal: one pill row per flavor option, styled like the
     product-page flavor selector — color swatch dot + label,
     selected pill filled neon yellow. Swatches: Lemon #f9ee55,
     Raspberry #db353d, Original #471d1c (Vital) / #0b3b26
     (Kinesis; detected from the option name or product title).
     Unknown flavors render without a dot. "Save flavor" arms
     only when the selection differs; saves via the existing
     line-update call (one-time policy preserved).
   - DEEP LINK: ?pg_flavor=1 auto-opens the flavor modal once
     the portal is ready; stashed in sessionStorage so it
     survives login + the list forward; cleared on use.
   - (CSS v31 also returns "Get it now" to solid red so the
     yellow flavor CTA stands alone.)

   v35 CHANGES
   - applyPolicyBadges also tags one-time ROWS with
     .pg-row-onetime (idempotent class toggle, safe category)
     so CSS v29 can slot the badge under the title/subtitle on
     mobile without touching normal rows.
   - KNOWN SERVER ISSUE (for the Awtomic support email): any
     line edit — ours or Awtomic's own native edit drawer —
     reprices a one-time add-on line to the add-on discount.
     Confirmed via native capture. Not fixable client-side.

   v34 CHANGES
   - ADD-ON DISCOUNT SPLIT BY GROUP. Awtomic applies one flat
     discount per add-on group to every add (subscription AND
     one-time), so the global 10% was also discounting one-time
     adds. Fix: two identical groups with different discount
     config, and adds are routed by policy:
       SUBSCRIPTION -> "Subscription Portal Add-ons"
                       (inherits global 10%)
       ONETIME      -> "Subscription Portal Add-ons (one-time)"
                       (discount override: none)
     Both ids from the Awtomic admin.

   v33 CHANGES
   - ONE-TIME LINE PRICING FIX. Discovered in testing: POSTing
     a line update WITHOUT lineSellingPolicy makes Awtomic
     reprice the line at the subscription discount — including
     one-time lines (a $45 one-time line dropped to $40.50
     after a same-variant swap). Fixes:
     (a) one-time rows are detected via the rendered One-time
         badge, and their Update/Swap API calls now include
         lineSellingPolicy:'ONETIME' to preserve retail
         pricing. EDUCATED GUESS (same category as the tested
         quantity:0 removal): the captured update payload came
         from a subscription line. Fails loudly to console if
         wrong; native flow untouched.
     (b) the swap modal shows RETAIL prices (with compare-at
         strike when present) for one-time lines, with a
         matching subtitle, instead of subscription prices.
     Subscription-line payloads are unchanged.
   - NOTE: subscribe-ADDS landing at retail ($45 not $40.50) is
     NOT addressed here — the add payload is byte-identical to
     the captured native request, so that price is server-side:
     check the Awtomic add-on group's discount override config.

   v32 CHANGES
   - upgradeThumbs(): Awtomic requests product thumbnails at
     width=128 from the Shopify CDN (confirmed via outerHTML);
     src is swapped to width=512 so the 2x thumbnail size in
     CSS v27 stays sharp. Idempotent attribute change on a
     React-owned element (proven-safe category).

   v31 CHANGES
   - Delay-modal options DEDUPED by label: the native skip menu
     is rendered in multiple places in the DOM (all sharing
     .bundle-action-item--skip), so v30's modal listed every
     option three times. First occurrence wins; clicking any
     copy fires the same handler.

   v30 CHANGES
   - Bottom "Skip next order" button replaced by "Skip or delay
     next order", which opens a branded modal listing Awtomic's
     native skip/delay options (Skip next delivery / Delay 2, 4,
     6, 8 weeks). Options are read live from the native action
     list (.bundle-action-item--skip) and matched BY TEXT — the
     five native items all share one class, so text is the only
     reliable selector. New options Awtomic adds appear in the
     modal automatically.
   - Native bottom Pause button is hidden via CSS (v25); it
     stays in the DOM as fallback, and pause remains available
     inside Awtomic's cancel-mitigation flow.
   - No API calls: each modal option programmatically clicks the
     corresponding native menu item (proven pattern). Awtomic
     updates the date in place and shows its own toast + Undo.

   v14 CHANGES
   - Custom "Add extras" modal (same design as swap) with per-
     card variant dropdowns and TWO actions: Subscribe (10% off
     price shown) or One-time (retail). Uses the captured
     add-line API (lineSellingPolicy + addonGroupId).
   - Apiary/merch products show only the One-time option.
   - Row treatment (dropdowns, swap, links, strikes) now covers
     ALL product rows including add-on lines.
   - Remove (\u00d7) button on each row. NOTE: removal payload is
     quantity:0 (unverified guess) — if it errors, capture the
     native remove request and report back.
   - Recurring/One-time labels on rows, now IDEMPOTENT (v15's
     remove-and-recreate chips looped with the MutationObserver
     and froze the page).
   - Observer debounced as a permanent freeze-proofing measure.
   - Totals block labels fixed; compare-at strike sums every
     line, re-read fresh on each render.
   - Total column hidden via CSS (Price remains).
   ============================================================ */

(function () {
  'use strict';

  /* Classic Shopify account pages are unused entirely — every
     arrival (Awtomic's unknown-email bounce, Google results,
     old bookmarks) goes to the portal login. The Awtomic bounce
     (keepShopifyLogin marker) carries the not-found notice. */
  if (window.location.pathname.indexOf('/account/login') === 0 ||
      window.location.pathname.indexOf('/account/register') === 0) {
    var isAwtomicBounce =
      window.location.search.indexOf('keepShopifyLogin') !== -1;
    window.location.replace('/tools/bundle-subscriptions' +
      (isAwtomicBounce ? '?pg_nosub=1' : ''));
    return;
  }

  if (!window.location.pathname.includes('/tools/bundle-subscriptions')) return;

  /* ────────────────────────────────────────────────────────── */
  /* CONFIG                                                     */

  var SWAP_COLLECTION_HANDLES = ['nectar', 'bundles'];

  /* Collections offered in the ADD EXTRAS modal. VERIFY the
     Apiary handle and that each is published to Online Store. */
  var ADDON_COLLECTION_HANDLES = ['nectar', 'bundles', 'apiary'];

  /* Products from these collections get ONE-TIME only (no
     selling plan → subscribe-add would fail). */
  var ONETIME_ONLY_COLLECTIONS = ['apiary'];

  /* Two Awtomic add-on groups, identical collections,
     different discount config — Awtomic prices adds by the
     group they're posted to (one flat rate per group, applied
     to sub AND one-time adds alike, hence the split):
     - SUBSCRIPTION group: inherits the global 10% add-on
       discount (id from the captured native add request)
     - ONETIME group: discount override = none (id from the
       Awtomic admin) */
  var ADDON_GROUP_ID_SUBSCRIPTION = '733dd647-d25f-4807-9744-be637069bc11';
  var ADDON_GROUP_ID_ONETIME = 'f0d4b264-54b7-44fc-b0f3-08db62dce0a2';

  var SWAPPABLE_HANDLES = ['vital-nectar', 'kinesis-nectar'];

  var SUBSCRIPTION_DISCOUNT_PCT = 10;
  var RELOAD_GRACE_MS = 600;
  /* ────────────────────────────────────────────────────────── */

  var API_BASE = 'https://shapi.bundlepayments.com';
  var DEBUG_TAG = '[pg-portal]';

  /* ── helpers ─────────────────────────────────────────────── */

  function getAuth() {
    try {
      var raw = localStorage.getItem('awtomicApiAuth');
      if (!raw) return null;
      var auth = JSON.parse(raw);
      return (auth && auth.authToken && auth.customerId) ? auth : null;
    } catch (e) { return null; }
  }

  function getSubscriptionId() {
    return new URLSearchParams(window.location.search).get('subscriptionId');
  }

  function numericId(gid) {
    var m = String(gid).match(/(\d+)\s*$/);
    return m ? m[1] : null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function money(n) { return '$' + n.toFixed(2); }

  function subPrice(retail) {
    return retail * (1 - SUBSCRIPTION_DISCOUNT_PCT / 100);
  }

  function compareAtOf(variant) {
    if (!variant) return null;
    var c = parseFloat(variant.compare_at_price);
    var p = parseFloat(variant.price);
    if (!isNaN(c) && !isNaN(p) && c > p) return c;
    return null;
  }

  function reloadSoon() {
    setTimeout(function () { window.location.reload(); }, RELOAD_GRACE_MS);
  }

  /* ── list page auto-skip ─────────────────────────────────── */

  /* PERSISTENT watcher: Awtomic reaches the list page via
     client-side navigation after login (no page load), so a
     one-shot poll misses it. This ticks quietly forever and
     forwards single-contract customers whenever the list is
     showing — any arrival path. Multi-contract: stands down. */
  function relabelLogin() {
    var nodes = document.querySelectorAll('#bundle-app p, #bundle-app h2, #bundle-app span');
    nodes.forEach(function (n) {
      if (n.children.length === 0 &&
          n.textContent.trim() === 'Enter your email address to get started') {
        n.textContent = 'Enter your email to access your subscription portal';
      }
    });
  }

  function startListWatcher() {
    var forwarded = false;
    var multiKnown = sessionStorage.getItem('pgMulti') === '1';
    var docEl = document.documentElement;

    setInterval(function () {
      var authed = getAuth();
      if (authed) clearNoSubState();
      if (!authed) relabelLogin();

      /* on a subscription detail page: reveal content, and hide
         the useless list breadcrumb for single-sub customers
         (only multi-sub sessions ever pass through the list) */
      if (getSubscriptionId()) {
        docEl.classList.remove('pg-hide-list');
        docEl.classList.toggle('pg-single-sub', !multiKnown);
        return;
      }

      /* logged out (login/verification screens): show as normal */
      if (!authed) {
        docEl.classList.remove('pg-hide-list');
        return;
      }

      /* logged in, no subscription open → the list is (about to
         be) showing. Hide it while we decide. */
      if (multiKnown) {
        docEl.classList.remove('pg-hide-list'); /* they need it */
      } else {
        docEl.classList.add('pg-hide-list');
      }

      if (forwarded) return;

      var links = Array.prototype.slice.call(
        document.querySelectorAll('a[href*="subscriptionId="]')
      );
      var ids = {};
      links.forEach(function (l) {
        var m = String(l.href).match(/subscriptionId=(\d+)/);
        if (m) ids[m[1]] = l.href;
      });
      var keys = Object.keys(ids);
      if (keys.length === 1) {
        forwarded = true;
        window.location.replace(ids[keys[0]]);
        return;
      }
      if (keys.length > 1) {
        multiKnown = true;
        sessionStorage.setItem('pgMulti', '1');
        docEl.classList.remove('pg-hide-list');
        return;
      }

      var manageBtns = Array.prototype.slice.call(
        document.querySelectorAll('button')
      ).filter(function (b) {
        return b.textContent.trim().toLowerCase() === 'manage';
      });
      if (manageBtns.length === 1) {
        forwarded = true;
        manageBtns[0].click();
        return;
      }
      if (manageBtns.length > 1) {
        multiKnown = true;
        sessionStorage.setItem('pgMulti', '1');
        docEl.classList.remove('pg-hide-list');
      }
    }, 150);
  }

  /* ── line discovery (confirmed shape + selling policy) ───── */

  function normalizeLine(L) {
    var lineGid = L.SubscriptionContractLineId || L.subscriptionContractLineId ||
                  L.id || L.Id || null;
    var variantGid = L.VariantId || L.variantId ||
                     L.ProductVariantId || L.productVariantId || null;
    if (!lineGid || String(lineGid).indexOf('SubscriptionLine') === -1) return null;
    if (!variantGid || String(variantGid).indexOf('ProductVariant') === -1) return null;
    var qty = L.Quantity || L.quantity;
    var policy = L.LineSellingPolicy || L.lineSellingPolicy || 'SUBSCRIPTION';
    return {
      id: lineGid,
      variantId: numericId(variantGid),
      quantity: (typeof qty === 'number' && qty > 0) ? qty : 1,
      policy: String(policy).toUpperCase()
    };
  }

  function findLines() {
    var details = window.Awtomic && window.Awtomic.subscriptionDetails;
    if (details && Array.isArray(details.Lines)) {
      var mapped = details.Lines.map(normalizeLine).filter(Boolean);
      if (mapped.length) return mapped;
    }
    var source = window.Awtomic;
    if (!source || typeof source !== 'object') return [];
    var found = [];
    var seen = new Set();
    function walk(node, depth) {
      if (!node || typeof node !== 'object' || depth > 8 || seen.has(node)) return;
      seen.add(node);
      if (Array.isArray(node)) {
        node.forEach(function (child) {
          if (child && typeof child === 'object') {
            var line = normalizeLine(child);
            if (line) { found.push(line); return; }
          }
          walk(child, depth + 1);
        });
      } else {
        Object.keys(node).forEach(function (k) { walk(node[k], depth + 1); });
      }
    }
    try { walk(source, 0); } catch (e) { /* ignore */ }
    return found;
  }

  function customerFirstName() {
    try {
      var d = window.Awtomic && window.Awtomic.subscriptionDetails;
      var name = d && d.Customer && d.Customer.FirstName;
      return (typeof name === 'string' && name.trim()) ? name.trim() : null;
    } catch (e) { return null; }
  }

  /* ── state + data loading ────────────────────────────────── */

  var state = {
    auth: null,
    subId: null,
    lines: [],
    products: [],
    swapProducts: [],
    addonProducts: [],
    onetimeOnlyIds: new Set(),
    productsReady: false
  };

  function fetchProducts() {
    return fetch('/products.json?limit=250')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        state.products = (data && data.products) || [];
        state.productsReady = true;
      });
  }

  function fetchCollectionSet(handles, onetimeOnlySet) {
    var seenIds = new Set();
    var merged = [];
    return handles.reduce(function (chain, handle) {
      return chain.then(function () {
        return fetch('/collections/' + handle + '/products.json?limit=250')
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function (data) {
            ((data && data.products) || []).forEach(function (p) {
              if (onetimeOnlySet && ONETIME_ONLY_COLLECTIONS.indexOf(handle) !== -1) {
                onetimeOnlySet.add(p.id);
              }
              if (!seenIds.has(p.id)) {
                seenIds.add(p.id);
                merged.push(p);
              }
            });
          })
          .catch(function (err) {
            console.warn(DEBUG_TAG, 'collection "' + handle + '" failed (' +
              err.message + ') \u2014 is it published to Online Store?');
          });
      });
    }, Promise.resolve()).then(function () { return merged; });
  }

  function findProductByVariant(variantId) {
    for (var i = 0; i < state.products.length; i++) {
      var p = state.products[i];
      for (var j = 0; j < (p.variants || []).length; j++) {
        if (String(p.variants[j].id) === String(variantId)) {
          return { product: p, variant: p.variants[j] };
        }
      }
    }
    return null;
  }

  function swappableProducts() {
    if (state.swapProducts.length) return state.swapProducts;
    if (SWAPPABLE_HANDLES.length) {
      var list = [];
      SWAPPABLE_HANDLES.forEach(function (h) {
        var p = state.products.find(function (x) { return x.handle === h; });
        if (p) list.push(p);
      });
      return list;
    }
    return state.products;
  }

  function addonProducts() {
    if (state.addonProducts.length) return state.addonProducts;
    return swappableProducts();
  }

  /* ── shared: variant option selects ──────────────────────── */

  function optionValues(product, opt, idx) {
    var values = opt.values;
    if (!values || !values.length) {
      values = [];
      (product.variants || []).forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
    }
    return values;
  }

  function isFlavorOption(opt) {
    return /flavor/i.test((opt && opt.name) || '');
  }

  /* flavor option groups with 2+ values: [{index, name, values}] */
  function flavorOptions(product) {
    var out = [];
    (product.options || []).forEach(function (opt, idx) {
      if (!isFlavorOption(opt)) return;
      var values = optionValues(product, opt, idx);
      if (values.length < 2) return;
      out.push({ index: idx + 1, name: opt.name, values: values });
    });
    return out;
  }

  /* product-page flavor swatch colors */
  function flavorSwatch(value, kinesisContext) {
    var v = String(value).toLowerCase();
    if (v.indexOf('lemon') !== -1) return '#f9ee55';
    if (v.indexOf('raspberry') !== -1) return '#db353d';
    if (v.indexOf('original') !== -1) {
      return kinesisContext ? '#0b3b26' : '#471d1c';
    }
    return null; /* unknown flavor: pill renders without a dot */
  }

  function buildOptionSelects(product, presetVariant, onChange, includeFn) {
    var container = el('div', 'pg-optrow');
    var selects = [];

    (product.options || []).forEach(function (opt, idx) {
      if (includeFn && !includeFn(opt, idx)) return;
      var values = optionValues(product, opt, idx);
      if (values.length < 2) return; /* single-value: no dropdown */

      var field = el('label', 'pg-variant-field');
      field.appendChild(el('span', 'pg-variant-label', opt.name));
      var select = el('select', 'pg-variant-select');
      select.setAttribute('data-option-index', String(idx + 1));

      values.forEach(function (val) {
        var o = el('option', null, val);
        o.value = val;
        if (presetVariant && presetVariant['option' + (idx + 1)] === val) o.selected = true;
        select.appendChild(o);
      });

      if (onChange) select.addEventListener('change', onChange);
      field.appendChild(select);
      container.appendChild(field);
      selects.push(select);
    });

    function getVariant() {
      var chosen = {};
      selects.forEach(function (s) {
        chosen['option' + s.getAttribute('data-option-index')] = s.value;
      });
      var base = presetVariant || (product.variants && product.variants[0]) || {};
      for (var n = 1; n <= 3; n++) {
        if (!(('option' + n) in chosen) && base['option' + n]) {
          chosen['option' + n] = base['option' + n];
        }
      }
      return (product.variants || []).find(function (v) {
        return [1, 2, 3].every(function (n) {
          var key = 'option' + n;
          return !v[key] || !chosen[key] || v[key] === chosen[key];
        });
      }) || null;
    }

    return { node: container, getVariant: getVariant, hasSelects: selects.length > 0, selects: selects };
  }

  /* ── API calls ───────────────────────────────────────────── */

  function linesEndpoint() {
    return API_BASE + '/customers/' + state.auth.customerId +
      '/subscriptions/' + state.subId + '/lines';
  }

  function postLines(lines) {
    return fetch(linesEndpoint(), {
      method: 'POST',
      headers: {
        'authorization': state.auth.authToken,
        'content-type': 'application/json'
      },
      body: JSON.stringify({ lines: lines, forceSyncUpdate: true })
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
    });
  }

  function saveLineVariant(line, variantNumericId, oneTime) {
    var input = {
      productVariantId: 'gid://shopify/ProductVariant/' + variantNumericId,
      quantity: line.quantity
    };
    /* Omitting lineSellingPolicy makes the server reprice the
       line at the subscription discount (observed in testing:
       a $45 one-time line became $40.50). Send it explicitly
       for one-time lines to keep retail pricing. EDUCATED
       GUESS on an update payload — if it errors, capture the
       native one-time edit request. Subscription lines keep
       the original captured payload untouched. */
    if (oneTime) input.lineSellingPolicy = 'ONETIME';
    return postLines([{ id: line.id, input: input }]);
  }

  function addLine(variantNumericId, policy) {
    return postLines([{
      id: null,
      input: {
        quantity: 1,
        productVariantId: 'gid://shopify/ProductVariant/' + variantNumericId,
        isAddon: true,
        lineSellingPolicy: policy, /* 'SUBSCRIPTION' | 'ONETIME' */
        addonGroupId: policy === 'ONETIME' ?
          ADDON_GROUP_ID_ONETIME : ADDON_GROUP_ID_SUBSCRIPTION
      }
    }]);
  }

  /* UNVERIFIED: quantity 0 as removal. If this errors, capture
     the native remove request so the payload can be corrected. */
  function removeLine(line) {
    return postLines([{
      id: line.id,
      input: {
        productVariantId: 'gid://shopify/ProductVariant/' + line.variantId,
        quantity: 0
      }
    }]);
  }

  /* ── generic product-picker modal (swap + add share it) ──── */

  function openPickerModal(opts) {
    var stale = document.querySelector('.pg-swap-overlay');
    if (stale) stale.remove();

    var overlay = el('div', 'pg-swap-overlay');
    var modal = el('div', 'pg-swap-modal');

    var header = el('div', 'pg-swap-header');
    header.appendChild(el('h3', 'pg-swap-title', opts.title));
    var closeBtn = el('button', 'pg-swap-close', '\u00d7');
    closeBtn.type = 'button';
    header.appendChild(closeBtn);
    modal.appendChild(header);

    modal.appendChild(el('p', 'pg-swap-sub', opts.subtitle));

    var grid = el('div', 'pg-swap-grid');
    var busy = false;

    opts.products.forEach(function (product) {
      var card = el('div', 'pg-swap-card');
      var productUrl = '/products/' + product.handle;

      var imgWrap = el('div', 'pg-swap-imgwrap');
      var imgLink = document.createElement('a');
      imgLink.href = productUrl;
      imgLink.target = '_blank';
      imgLink.rel = 'noopener';
      var imgSrc = (product.images && product.images[0] && product.images[0].src) || '';
      if (imgSrc) {
        var img = document.createElement('img');
        img.src = imgSrc;
        img.alt = product.title;
        img.loading = 'lazy';
        imgLink.appendChild(img);
      }
      imgWrap.appendChild(imgLink);
      card.appendChild(imgWrap);

      var nameEl = el('h4', 'pg-swap-name');
      var nameLink = document.createElement('a');
      nameLink.href = productUrl;
      nameLink.target = '_blank';
      nameLink.rel = 'noopener';
      nameLink.textContent = product.title;
      nameEl.appendChild(nameLink);
      card.appendChild(nameEl);

      var opts2 = buildOptionSelects(product, null, refresh);
      card.appendChild(opts2.node);

      var priceRow = el('div', 'pg-swap-pricerow');
      card.appendChild(priceRow);

      var actionsBox = el('div', 'pg-add-actions');
      card.appendChild(actionsBox);

      function refresh() {
        var v = opts2.getVariant();
        priceRow.textContent = '';
        actionsBox.textContent = '';
        if (!v) return;
        var retail = parseFloat(v.price);
        if (isNaN(retail)) return;
        var compare = compareAtOf(v);

        opts.renderActions({
          card: card,
          variant: v,
          retail: retail,
          compare: compare,
          priceRow: priceRow,
          actionsBox: actionsBox,
          setBusy: function (b, label, btn) {
            busy = b;
            modal.classList.toggle('pg-swap-busy', b);
            if (btn && label) btn.textContent = label;
          },
          isBusy: function () { return busy; }
        });
      }
      refresh();

      grid.appendChild(card);
    });

    modal.appendChild(grid);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKey);
  }

  function openSwapModal(line, oneTime) {
    openPickerModal({
      title: 'Swap product',
      subtitle: oneTime ?
        'Prices shown are one-time prices.' :
        'Prices shown are your subscription price.',
      products: swappableProducts(),
      renderActions: function (ctx) {
        /* one-time lines pay retail; subscription lines pay
           the discounted price. Strike: compare-at when it
           exists; for subscription lines retail works as the
           strike too, but retail-vs-retail is meaningless on
           one-time lines, so no strike there without compare. */
        if (oneTime) {
          if (ctx.compare !== null) {
            ctx.priceRow.appendChild(el('span', 'pg-swap-strike', money(ctx.compare)));
          }
          ctx.priceRow.appendChild(el('span', 'pg-swap-price', money(ctx.retail)));
        } else {
          ctx.priceRow.appendChild(el('span', 'pg-swap-strike',
            money(ctx.compare !== null ? ctx.compare : ctx.retail)));
          ctx.priceRow.appendChild(el('span', 'pg-swap-price', money(subPrice(ctx.retail))));
        }

        var choose = el('button', 'pg-swap-choose', 'Choose this');
        choose.type = 'button';
        choose.addEventListener('click', function () {
          if (ctx.isBusy()) return;
          ctx.setBusy(true, 'Swapping\u2026', choose);
          saveLineVariant(line, ctx.variant.id, oneTime)
            .then(function () { reloadSoon(); })
            .catch(function (err) {
              console.error(DEBUG_TAG, 'swap failed:', err);
              ctx.setBusy(false, 'Choose this', choose);
              alert('Sorry, that swap didn\u2019t save. Please try again.');
            });
        });
        ctx.actionsBox.appendChild(choose);
      }
    });
  }

  function openAddModal() {
    openPickerModal({
      title: 'Add to your next order',
      subtitle: 'Subscribe and save, or add it once to your next box.',
      products: addonProducts(),
      renderActions: function (ctx) {
        var oneTimeOnly = state.onetimeOnlyIds.has(
          (findProductByVariant(ctx.variant.id) || { product: {} }).product.id
        );

        if (ctx.compare !== null) {
          ctx.priceRow.appendChild(el('span', 'pg-swap-strike', money(ctx.compare)));
        }

        if (!oneTimeOnly) {
          var subBtn = el('button', 'pg-swap-choose pg-add-sub',
            'Subscribe \u2014 ' + money(subPrice(ctx.retail)));
          subBtn.type = 'button';
          subBtn.addEventListener('click', function () {
            if (ctx.isBusy()) return;
            ctx.setBusy(true, 'Adding\u2026', subBtn);
            addLine(ctx.variant.id, 'SUBSCRIPTION')
              .then(function () { reloadSoon(); })
              .catch(function (err) {
                console.error(DEBUG_TAG, 'add (subscription) failed:', err);
                ctx.setBusy(false, 'Subscribe \u2014 ' + money(subPrice(ctx.retail)), subBtn);
                alert('Sorry, that didn\u2019t save. Please try again.');
              });
          });
          ctx.actionsBox.appendChild(subBtn);
        }

        var oneBtn = el('button', 'pg-add-one',
          'One-time \u2014 ' + money(ctx.retail));
        oneBtn.type = 'button';
        oneBtn.addEventListener('click', function () {
          if (ctx.isBusy()) return;
          ctx.setBusy(true, 'Adding\u2026', oneBtn);
          addLine(ctx.variant.id, 'ONETIME')
            .then(function () { reloadSoon(); })
            .catch(function (err) {
              console.error(DEBUG_TAG, 'add (one-time) failed:', err);
              ctx.setBusy(false, 'One-time \u2014 ' + money(ctx.retail), oneBtn);
              alert('Sorry, that didn\u2019t save. Please try again.');
            });
        });
        ctx.actionsBox.appendChild(oneBtn);
      }
    });
  }

  /* ── change-flavor modal ─────────────────────────────────── */

  function openFlavorModal(row) {
    var vid = rowVariantId(row);
    var match = vid ? findProductByVariant(vid) : null;
    var line = match ? rowLine(row) : null;
    if (!match || !line) return;
    var product = match.product;
    var current = match.variant;
    var groups = flavorOptions(product);
    if (!groups.length) return;

    var stale = document.querySelector('.pg-flavor-overlay');
    if (stale) stale.remove();

    var overlay = el('div', 'pg-swap-overlay pg-flavor-overlay');
    var modal = el('div', 'pg-flavor-modal');

    var header = el('div', 'pg-swap-header');
    header.appendChild(el('h3', 'pg-swap-title', 'Change flavor'));
    var closeBtn = el('button', 'pg-swap-close', '\u00d7');
    closeBtn.type = 'button';
    header.appendChild(closeBtn);
    modal.appendChild(header);

    modal.appendChild(el('p', 'pg-swap-sub', product.title));

    /* chosen flavor per option index; non-flavor options (pack
       size) carry over from the current variant */
    var chosen = {};
    var pillsByGroup = {};

    var save = el('button', 'pg-flavor-save', 'Save flavor');
    save.type = 'button';
    save.disabled = true;

    function resolveVariant() {
      return (product.variants || []).find(function (v) {
        return [1, 2, 3].every(function (n) {
          var want = (n in chosen) ? chosen[n] : current['option' + n];
          return !v['option' + n] || !want || v['option' + n] === want;
        });
      }) || null;
    }

    function updateSave() {
      var v = resolveVariant();
      var changed = v && String(v.id) !== String(current.id);
      save.disabled = !changed;
      save.classList.toggle('pg-flavor-save--armed', !!changed);
    }

    groups.forEach(function (group) {
      /* "Original" is VN red in Vital context, KN green in
         Kinesis context: read the option name first (Bundle),
         fall back to the product title (standalone Kinesis) */
      var kinesisCtx = /kinesis/i.test(group.name) ||
        (!/vital/i.test(group.name) && /kinesis/i.test(product.title || ''));

      chosen[group.index] = current['option' + group.index];
      var box = el('div', 'pg-flavor-group');
      box.appendChild(el('span', 'pg-flavor-group-label', group.name));
      var pills = el('div', 'pg-flavor-pills');
      pillsByGroup[group.index] = [];
      group.values.forEach(function (val) {
        var pill = el('button', 'pg-flavor-pill');
        pill.type = 'button';
        var dotColor = flavorSwatch(val, kinesisCtx);
        if (dotColor) {
          var dot = el('span', 'pg-flavor-dot');
          dot.style.background = dotColor;
          pill.appendChild(dot);
        }
        pill.appendChild(document.createTextNode(val));
        if (val === chosen[group.index]) pill.classList.add('pg-flavor-pill--active');
        pill.addEventListener('click', function () {
          chosen[group.index] = val;
          pillsByGroup[group.index].forEach(function (pn) {
            pn.classList.toggle('pg-flavor-pill--active', pn === pill);
          });
          updateSave();
        });
        pillsByGroup[group.index].push(pill);
        pills.appendChild(pill);
      });
      box.appendChild(pills);
      modal.appendChild(box);
    });

    save.addEventListener('click', function () {
      var v = resolveVariant();
      if (!v || save.disabled) return;
      save.disabled = true;
      save.textContent = 'Saving\u2026';
      modal.classList.add('pg-swap-busy');
      saveLineVariant(line, v.id, isRowOneTime(row))
        .then(function () { reloadSoon(); })
        .catch(function (err) {
          console.error(DEBUG_TAG, 'flavor change failed:', err);
          modal.classList.remove('pg-swap-busy');
          save.textContent = 'Save flavor';
          updateSave();
          alert('Sorry, that change didn\u2019t save. Please try again.');
        });
    });
    modal.appendChild(save);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKey);
  }

  /* ── per-row UI ──────────────────────────────────────────── */

  function productRows() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.bundle-details-table tbody tr, .bundle-table__body-row')
    ).filter(function (row, i, arr) {
      return arr.indexOf(row) === i && rowVariantId(row);
    });
  }

  function rowVariantId(row) {
    var id = null;
    row.classList.forEach(function (c) {
      if (c.indexOf('ProductVariant') !== -1) id = numericId(c);
    });
    return id;
  }

  /* one-time detection: the page global has no line policy
     (confirmed), so the rendered "One-time" badge is truth.
     Read at CLICK time — badges exist once applyPolicyBadges
     has run, and reading late avoids ordering assumptions. */
  function isRowOneTime(row) {
    return !!row.querySelector('.pg-onetime-badge');
  }

  function rowLine(row) {
    var vid = rowVariantId(row);
    if (!vid) return null;
    return state.lines.find(function (l) {
      return String(l.variantId) === String(vid);
    }) || null;
  }

  function buildSelectorBlock(row, line, match) {
    var product = match.product;
    var current = match.variant;

    var wrap = el('div', 'pg-variant-bar');

    /* flavor-capable products: compact yellow CHANGE FLAVOR
       button in place of the flavor dropdowns; pack size
       stays inline */
    if (flavorOptions(product).length) {
      var flavorBtn = el('button', 'pg-flavor-btn', 'Change flavor');
      flavorBtn.type = 'button';
      flavorBtn.addEventListener('click', function () {
        openFlavorModal(row);
      });
      wrap.appendChild(flavorBtn);
    }

    var opts = buildOptionSelects(product, current, onSelectionChange,
      function (opt) { return !isFlavorOption(opt); });
    while (opts.node.firstChild) wrap.appendChild(opts.node.firstChild);

    var actions = el('div', 'pg-variant-actions');
    var updateBtn = el('button', 'pg-variant-update', 'Update');
    updateBtn.type = 'button';
    updateBtn.disabled = true;
    if (!opts.hasSelects) updateBtn.style.display = 'none';
    var swapBtn = el('button', 'pg-variant-swap', 'Swap product');
    swapBtn.type = 'button';
    actions.appendChild(updateBtn);
    actions.appendChild(swapBtn);
    wrap.appendChild(actions);

    function onSelectionChange() {
      var v = opts.getVariant();
      var changed = v && String(v.id) !== String(current.id);
      updateBtn.disabled = !changed;
      updateBtn.classList.toggle('pg-variant-update--armed', !!changed);
    }

    updateBtn.addEventListener('click', function () {
      var v = opts.getVariant();
      if (!v) return;
      updateBtn.disabled = true;
      updateBtn.textContent = 'Saving\u2026';
      opts.selects.forEach(function (s) { s.disabled = true; });
      saveLineVariant(line, v.id, isRowOneTime(row))
        .then(function () { reloadSoon(); })
        .catch(function (err) {
          console.error(DEBUG_TAG, 'variant update failed:', err);
          updateBtn.textContent = 'Update';
          updateBtn.disabled = false;
          opts.selects.forEach(function (s) { s.disabled = false; });
          alert('Sorry, that change didn\u2019t save. Please try again.');
        });
    });

    swapBtn.addEventListener('click', function () {
      openSwapModal(line, isRowOneTime(row));
    });

    return wrap;
  }

  function rewireProductLinks(row, product) {
    if (row.dataset.pgLinksWired) return;
    var url = '/products/' + product.handle;
    var cell = row.querySelector('.body-row__product-detail') ||
               row.querySelector('.bundle-product-details');
    if (!cell) return;
    cell.addEventListener('click', function (e) {
      if (e.target.closest('.pg-variant-bar')) return; /* our UI */
      /* suppress Awtomic's drawer everywhere in the cell... */
      e.stopPropagation();
      e.preventDefault();
      /* ...but only NAVIGATE from the thumbnail or the title */
      var clickable = e.target.closest('img') ||
                      e.target.closest('.bundle-product-avatar') ||
                      e.target.closest('.bundle-product-name');
      if (clickable) window.open(url, '_blank', 'noopener');
    }, true);
    cell.querySelectorAll('a').forEach(function (a) {
      a.setAttribute('href', url);
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
    });
    row.dataset.pgLinksWired = '1';
  }

  function injectRemoveButton(row, line) {
    if (row.querySelector('.pg-remove')) return;
    var lastCell = row.querySelector('td:last-child');
    if (!lastCell) return;
    var btn = el('button', 'pg-remove', '\u00d7');
    btn.type = 'button';
    btn.title = 'Remove from subscription';
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!confirm('Remove this item from your subscription?')) return;
      btn.disabled = true;
      removeLine(line)
        .then(function () { reloadSoon(); })
        .catch(function (err) {
          console.error(DEBUG_TAG, 'remove failed (payload guess may be wrong):', err);
          btn.disabled = false;
          alert('Sorry, that didn\u2019t work. Please contact us to remove this item.');
        });
    });
    lastCell.appendChild(btn);
  }

  /* ── one-time badges (grouping via sorting is off: moving
     rows React owns crashes the portal) ────────────────────── */

  function applyPolicyBadges() {
    /* Tag Awtomic's native "One-time" text by its content and
       let CSS pill it. No dependence on line policy data. */
    productRows().forEach(function (row) {
      var cell = row.querySelector('.body-row__product-detail') || row;
      cell.querySelectorAll('p, span, div, strong').forEach(function (n) {
        if (n.classList.contains('pg-onetime-badge')) return;
        if (n.children.length === 0 && n.textContent.trim() === 'One-time') {
          n.classList.add('pg-onetime-badge');
        }
      });
      /* row-level marker for CSS (mobile badge placement).
         Idempotent toggle; legacy pg-line-onetime is gone. */
      row.classList.toggle('pg-row-onetime',
        !!row.querySelector('.pg-onetime-badge'));
    });
  }


  /* ── extras row + rebuilt totals ─────────────────────────── */

  function nativeSeeAllButton() {
    var container = document.querySelector('.addon-container');
    if (!container) return null;
    var btns = container.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) {
      if (/see all/i.test(btns[i].textContent)) return btns[i];
    }
    return null;
  }

  function injectExtrasRow() {
    var layout = document.querySelector('.bundle-subs-detail-layout');
    if (!layout || layout.querySelector('.pg-extras-row')) return;
    if (!nativeSeeAllButton() && !addonProducts().length) return;

    var box = el('button', 'pg-extras-row');
    box.type = 'button';
    box.appendChild(el('span', 'pg-extras-label', 'Add extras to next order'));
    box.appendChild(el('span', 'pg-extras-plus', '+'));
    box.addEventListener('click', function () {
      if (addonProducts().length) {
        openAddModal();
      } else {
        var b = nativeSeeAllButton();
        if (b) b.click();
      }
    });

    var table = layout.querySelector('.bundle-details-table');
    if (table && table.nextSibling) {
      table.parentNode.insertBefore(box, table.nextSibling);
    } else {
      layout.appendChild(box);
    }
  }

  function summaryCompareTotal() {
    var total = 0;
    var any = false;
    state.lines.forEach(function (l) {
      var match = findProductByVariant(l.variantId);
      if (!match) return;
      var compare = compareAtOf(match.variant);
      var retail = parseFloat(match.variant.price);
      if (compare !== null) {
        total += compare * l.quantity;
        any = true;
      } else if (!isNaN(retail)) {
        total += retail * l.quantity; /* no compare-at: count retail */
      }
    });
    return any ? total : null;
  }

  var KIND_LABELS = {
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    total: 'Total',
    discount: 'Discount',
    tax: 'Taxes',
    taxes: 'Taxes'
  };

  function buildTotals() {
    var layout = document.querySelector('.bundle-subs-detail-layout');
    if (!layout) return;
    var footerRows = document.querySelectorAll('.bundle-table__footer-row');
    if (!footerRows.length) return;

    var box = layout.querySelector('.pg-totals');
    if (!box) {
      box = el('div', 'pg-totals');
      var extras = layout.querySelector('.pg-extras-row');
      var anchor = extras || layout.querySelector('.bundle-details-table');
      if (anchor && anchor.nextSibling) {
        anchor.parentNode.insertBefore(box, anchor.nextSibling);
      } else {
        layout.appendChild(box);
      }
    }

    var compareTotal = summaryCompareTotal();
    var tmp = document.createElement('div');

    footerRows.forEach(function (row) {
      var kind = (row.className.match(/footer-row--(\w+)/) || [])[1] || '';
      var valueP = row.querySelector('td:last-child p');
      if (!valueP) return;
      var value = valueP.textContent.trim();
      var label = KIND_LABELS[kind] || kind || '';
      if (!label) return;

      var line = el('div', 'pg-totals-row pg-totals-row--' + kind);
      line.appendChild(el('span', 'pg-totals-label', label));

      var val = el('span', 'pg-totals-value');
      if ((kind === 'subtotal' || kind === 'total') && compareTotal !== null) {
        val.appendChild(el('span', 'bundle-striked-price', money(compareTotal)));
      }
      if (kind === 'shipping' && value === '$0.00') {
        val.appendChild(el('span', 'pg-free', 'FREE'));
      } else {
        val.appendChild(document.createTextNode(value));
      }
      line.appendChild(val);
      tmp.appendChild(line);

      if (kind === 'shipping') {
        tmp.appendChild(el('div', 'pg-totals-note pg-tax-note',
          'Taxes calculated at time of order'));
      }
    });

    if (box.innerHTML !== tmp.innerHTML) {
      box.innerHTML = tmp.innerHTML;
    }
  }

  /* ── bottom "Skip or delay next order" ───────────────────── */

  /* All five native menu items (Skip next delivery / Delay 2,
     4, 6, 8 weeks) share the class .bundle-action-item--skip —
     confirmed via outerHTML. Text content is the only way to
     tell them apart, so options are read and matched by text. */
  function nativeSkipItems() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.bundle-action-item--skip')
    ).filter(function (item) {
      return item.textContent.trim().length > 0;
    });
  }

  function openDelayModal() {
    var items = nativeSkipItems();
    if (!items.length) {
      console.warn(DEBUG_TAG,
        'no native skip/delay items found \u2014 delay modal aborted');
      return;
    }

    var stale = document.querySelector('.pg-delay-overlay');
    if (stale) stale.remove();

    /* the native skip menu is rendered in MULTIPLE places (top
       action list, Next Order card, ...) — same items each
       time. Dedupe by label, first occurrence wins; order is
       preserved (Skip first, then the delays). */
    var seen = {};
    var labels = [];
    items.forEach(function (item) {
      var label = item.textContent.trim();
      if (seen[label]) return;
      seen[label] = true;
      labels.push(label);
    });

    var overlay = el('div', 'pg-swap-overlay pg-delay-overlay');
    var modal = el('div', 'pg-delay-modal');

    var header = el('div', 'pg-swap-header');
    header.appendChild(el('h3', 'pg-swap-title', 'Skip or delay your next order'));
    var closeBtn = el('button', 'pg-swap-close', '\u00d7');
    closeBtn.type = 'button';
    header.appendChild(closeBtn);
    modal.appendChild(header);

    modal.appendChild(el('p', 'pg-swap-sub',
      'Push your next order back. Everything else about your subscription stays the same.'));

    var list = el('div', 'pg-delay-list');
    labels.forEach(function (label, i) {
      var opt = el('button',
        'pg-delay-opt' + (i === 0 ? ' pg-delay-opt--primary' : ''), label);
      opt.type = 'button';
      opt.addEventListener('click', function () {
        /* re-find by text at click time: React may have
           re-rendered the menu since the modal opened */
        var live = nativeSkipItems().find(function (n) {
          return n.textContent.trim() === label;
        });
        if (!live) {
          console.error(DEBUG_TAG,
            'native skip item "' + label + '" vanished \u2014 no action taken');
          close();
          alert('Sorry, that didn\u2019t work. Please try again.');
          return;
        }
        live.click(); /* Awtomic updates the date + shows toast/Undo */
        close();
      });
      list.appendChild(opt);
    });
    modal.appendChild(list);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKey);
  }

  function injectBottomSkip() {
    var layout = document.querySelector('.bundle-subs-detail-layout');
    if (!layout || layout.querySelector('.pg-skip-bottom')) return;
    var cancelBtn = layout.querySelector('.bundle-button--cancel');
    if (!cancelBtn) return;
    if (!nativeSkipItems().length) return; /* native flow only */
    var btn = el('button', 'pg-skip-bottom', 'Skip or delay next order');
    btn.type = 'button';
    btn.addEventListener('click', openDelayModal);
    cancelBtn.parentNode.insertBefore(btn, cancelBtn);
  }

  /* ── title + row strikes ─────────────────────────────────── */

  function restructureTitle() {
    var title = document.querySelector('#bundle-app .bundle-details-table-title');
    if (!title || title.dataset.pgTitled) return;
    var span = title.querySelector('span');
    var textNode = title.childNodes[0];
    if (!textNode || textNode.nodeType !== 3 || !span) return;
    var dateMatch = span.textContent.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
    if (!dateMatch) return;
    var name = customerFirstName();
    if (name) textNode.textContent = 'Hi ' + name + ',';
    span.textContent = '';
    span.appendChild(document.createTextNode('Your next order ships on '));
    span.appendChild(el('strong', 'pg-ship-date', dateMatch[1]));
    title.dataset.pgTitled = '1';
  }

  function fixRowStrikes() {
    productRows().forEach(function (row) {
      if (row.dataset.pgStrikesFixed) return;
      var vid = rowVariantId(row);
      if (!vid) return;
      var match = findProductByVariant(vid);
      if (!match) return;
      var compare = compareAtOf(match.variant);
      if (compare === null) return;
      var line = rowLine(row);
      var qty = line ? line.quantity : 1;
      var strikes = row.querySelectorAll('.bundle-striked-price');
      if (strikes[0]) {
        strikes[0].textContent = money(compare);
      } else {
        /* freshly added lines have no native strike element —
           create one in the price cell */
        var priceCell = row.querySelector('td:nth-child(3)');
        var host = priceCell && (priceCell.querySelector('p') || priceCell);
        if (host) {
          host.insertBefore(
            el('span', 'bundle-striked-price', money(compare)),
            host.firstChild
          );
        }
      }
      if (strikes[1]) strikes[1].textContent = money(compare * qty);
      row.dataset.pgStrikesFixed = '1';
    });
  }

  /* ── thumbnail sharpening ────────────────────────────────── */

  /* Awtomic requests product images at width=128 from the
     Shopify CDN (confirmed via outerHTML) — too soft for the
     2x display size (CSS v27). Re-request at width=512.
     Idempotent: once swapped, the test no longer matches. */
  function upgradeThumbs() {
    document.querySelectorAll(
      '.bundle-subs-detail-layout-column--left > .bundle-details-table img.bundle-variant-img'
    ).forEach(function (img) {
      var m = img.src.match(/([?&])width=(\d+)/);
      if (m && parseInt(m[2], 10) < 512) {
        img.src = img.src.replace(/([?&])width=\d+/, '$1width=512');
      }
    });
  }

  /* ── injection orchestration ─────────────────────────────── */

  function injectAll() {
    if (!state.productsReady) return;

    /* fresh read every pass so added/removed lines are seen */
    var fresh = findLines();
    if (fresh.length) state.lines = fresh;
    if (!state.lines.length) return;

    injectBottomSkip();
    restructureTitle();
    injectExtrasRow();
    buildTotals();
    fixRowStrikes();
    applyPolicyBadges();
    upgradeThumbs();

    productRows().forEach(function (row) {
      var vid = rowVariantId(row);
      var match = findProductByVariant(vid);
      if (!match) return;
      rewireProductLinks(row, match.product);
      var line = rowLine(row);
      if (!line) return;
      injectRemoveButton(row, line);
      if (row.querySelector('.pg-variant-bar')) return;
      var details = row.querySelector('.bundle-product-details');
      if (!details) return;
      details.appendChild(buildSelectorBlock(row, line, match));
    });

  }

  function relabelOrderNow() {
    document.querySelectorAll('.bundle-action-button-container > button')
      .forEach(function (b) {
        if (b.textContent.trim().toLowerCase() === 'order now') {
          b.textContent = 'Get it now';
        }
      });
  }

  /* ── injected styles ─────────────────────────────────────── */

  function injectStyles() {
    if (document.getElementById('pg-portal-styles')) return;
    var css = [
      '#bundle-app .awtomatic-line-edit-button{display:none!important;}',
      '#bundle-app .body-row__product-detail{cursor:default;}',
      '#bundle-app .body-row__product-detail img,#bundle-app .body-row__product-detail .bundle-product-name,#bundle-app .bundle-product-avatar{cursor:pointer;}',
      '#bundle-app .pg-hidden-policy{display:none!important;}',
      '#bundle-app .pg-onetime-badge{display:inline-block;background:#e4b245;color:#471c1c;font-family:\'Source Serif 4\',Georgia,serif;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;border-radius:4px;padding:3px 8px;margin-left:8px;vertical-align:middle;}',
      '#bundle-app div.pg-tax-note{margin-top:4px;text-align:right;white-space:nowrap;}',
      '#bundle-app .pg-remove{background:none;border:1.5px solid #d4c4a8;border-radius:50%;width:26px;height:26px;line-height:1;font-size:16px;color:#8a7060;cursor:pointer;margin-left:8px;}',
      '#bundle-app .pg-remove:hover{border-color:#471c1c;color:#471c1c;}',

      '.pg-swap-overlay{position:fixed;inset:0;z-index:99999;background:rgba(71,28,28,.55);display:flex;align-items:center;justify-content:center;padding:20px;}',
      '.pg-swap-modal{background:#fff;border-radius:14px;max-width:860px;width:100%;max-height:88vh;overflow:auto;padding:24px 24px 28px;font-family:\'Source Serif 4\',Georgia,serif;color:#471c1c;}',
      '.pg-swap-busy{opacity:.6;pointer-events:none;}',
      '.pg-swap-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;}',
      '.pg-swap-title{font-family:\'Playfair Display\',Georgia,serif;font-size:24px;font-weight:800;color:#471c1c;margin:0;letter-spacing:-0.02em;}',
      '.pg-swap-close{background:#faf5e1;border:none;border-radius:50%;width:34px;height:34px;font-size:20px;color:#471c1c;cursor:pointer;line-height:1;}',
      '.pg-swap-close:hover{background:#e4b245;}',
      '.pg-swap-sub{font-size:13px;color:#8a7060;margin:0 0 18px;}',
      '.pg-swap-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:16px;}',
      '.pg-swap-card{border:1px solid #d4c4a8;border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:10px;background:#faf8f0;}',
      '.pg-swap-imgwrap{background:#fff;border-radius:8px;aspect-ratio:1/1;overflow:hidden;display:flex;align-items:center;justify-content:center;}',
      '.pg-swap-imgwrap a{display:block;width:100%;height:100%;}',
      '.pg-swap-imgwrap img{width:100%;height:100%;object-fit:cover;display:block;}',
      '.pg-swap-name{font-family:\'Playfair Display\',Georgia,serif;font-size:16px;font-weight:700;margin:0;color:#471c1c;letter-spacing:-0.01em;}',
      '.pg-swap-name a{color:inherit;text-decoration:none;}',
      '.pg-swap-name a:hover{color:#e4b245;}',
      '.pg-swap-card .pg-optrow{display:flex;flex-direction:column;gap:8px;}',
      '.pg-swap-card .pg-variant-select{width:100%;}',
      '.pg-swap-pricerow{display:flex;align-items:baseline;gap:8px;min-height:18px;}',
      '.pg-swap-strike{font-family:\'Source Serif 4\',Georgia,serif;font-size:14px;color:#8a7060;text-decoration:line-through;}',
      '.pg-swap-price{font-family:\'Playfair Display\',Georgia,serif;font-size:19px;font-weight:800;color:#471c1c;}',
      '.pg-add-actions{display:flex;flex-direction:column;gap:8px;margin-top:auto;}',
      '.pg-swap-choose,.pg-add-sub{background:#471c1c;color:#faf8f0;border:none;border-radius:12px;font-family:\'Source Serif 4\',Georgia,serif;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:11px 16px;cursor:pointer;}',
      '.pg-swap-choose:hover,.pg-add-sub:hover{opacity:.92;}',
      '.pg-add-one{background:transparent;color:#471c1c;border:1.5px solid #471c1c;border-radius:12px;font-family:\'Source Serif 4\',Georgia,serif;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:10px 16px;cursor:pointer;}',
      '.pg-add-one:hover{background:#faf5e1;}',
      '@media (max-width:640px){.pg-swap-modal{padding:18px 14px 22px;max-height:92vh;}.pg-swap-grid{grid-template-columns:1fr;}}',

      '.pg-delay-modal{background:#fff;border-radius:14px;max-width:420px;width:100%;max-height:88vh;overflow:auto;padding:24px 24px 28px;font-family:\'Source Serif 4\',Georgia,serif;color:#471c1c;}',
      '.pg-delay-list{display:flex;flex-direction:column;gap:10px;margin-top:16px;}',
      '.pg-delay-opt{background:transparent;color:#471c1c;border:1.5px solid #471c1c;border-radius:12px;font-family:\'Source Serif 4\',Georgia,serif;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:13px 16px;cursor:pointer;width:100%;}',
      '.pg-delay-opt:hover{background:#faf5e1;}',
      '.pg-delay-opt--primary{background:#471c1c;color:#faf8f0;border:none;padding:14px 16px;}',
      '.pg-delay-opt--primary:hover{background:#471c1c;opacity:.92;}',
      '@media (max-width:640px){.pg-delay-modal{padding:18px 16px 22px;}}',

      '.pg-flavor-modal{background:#fff;border-radius:14px;max-width:480px;width:100%;max-height:88vh;overflow:auto;padding:24px 24px 28px;font-family:\'Source Serif 4\',Georgia,serif;color:#471c1c;}',
      '.pg-flavor-group{margin-top:16px;}',
      '.pg-flavor-group-label{display:block;font-family:\'Source Serif 4\',Georgia,serif;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8a7060;margin-bottom:8px;}',
      '.pg-flavor-pills{display:flex;flex-wrap:wrap;gap:10px;}',
      '.pg-flavor-pill{display:flex;align-items:center;justify-content:center;gap:9px;flex:1 1 0;min-width:118px;background:#fff;color:#471c1c;border:1.5px solid #471c1c;border-radius:12px;font-family:\'Source Serif 4\',Georgia,serif;font-size:15px;font-weight:700;padding:14px 10px;cursor:pointer;}',
      '.pg-flavor-pill:hover{background:#faf5e1;}',
      '.pg-flavor-pill--active{background:#F2DA49;}',
      '.pg-flavor-pill--active:hover{background:#F2DA49;}',
      '.pg-flavor-dot{width:15px;height:15px;border-radius:50%;flex:none;}',
      '.pg-flavor-save{width:100%;margin-top:20px;background:#d4c4a8;color:#fff;border:none;border-radius:12px;font-family:\'Source Serif 4\',Georgia,serif;font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:14px 18px;cursor:default;}',
      '.pg-flavor-save--armed{background:#471c1c;color:#faf8f0;cursor:pointer;}',
      '@media (max-width:640px){.pg-flavor-modal{padding:18px 16px 22px;}.pg-flavor-pills{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));}.pg-flavor-pill{min-width:0;}}'
    ].join('');
    var tag = document.createElement('style');
    tag.id = 'pg-portal-styles';
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  /* ── boot ────────────────────────────────────────────────── */

  function clearNoSubState() {
    var n = document.querySelector('.pg-nosub-notice');
    if (n) n.remove();
    if (window.location.search.indexOf('pg_nosub') !== -1) {
      var params = new URLSearchParams(window.location.search);
      params.delete('pg_nosub');
      var qs = params.toString();
      window.history.replaceState({}, '',
        window.location.pathname + (qs ? '?' + qs : ''));
    }
  }

  function injectNoSubNotice() {
    if (new URLSearchParams(window.location.search).get('pg_nosub') !== '1') return;
    if (getAuth() || getSubscriptionId()) { clearNoSubState(); return; }
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var host = document.getElementById('bundle-app') ||
                 document.getElementById('bundleapp-subscriptions-root');
      if (host && !document.querySelector('.pg-nosub-notice')) {
        var n = el('div', 'pg-nosub-notice',
          'We couldn\u2019t find a subscription for that email. ' +
          'Double-check the address, or reach out to us at info@pungao.co and we\u2019ll help you out.');
        host.insertBefore(n, host.firstChild);
        clearInterval(t);
      }
      if (tries > 40) clearInterval(t);
    }, 250);
  }

  function start() {
    console.log(DEBUG_TAG, 'v38 active');

    /* deep link (?pg_flavor=1): stash in sessionStorage so it
       survives login + the list-page forward, then clean the
       URL (keeps authToken/subscriptionId params intact) */
    if (window.location.search.indexOf('pg_flavor') !== -1) {
      sessionStorage.setItem('pgFlavor', '1');
      var fp = new URLSearchParams(window.location.search);
      fp.delete('pg_flavor');
      var fqs = fp.toString();
      window.history.replaceState({}, '',
        window.location.pathname + (fqs ? '?' + fqs : ''));
    }

    injectNoSubNotice();
    state.auth = getAuth();
    state.subId = getSubscriptionId();

    /* watcher must start BEFORE login exists — the page loads
       pre-auth, and the post-login list arrives via SPA nav */
    startListWatcher();

    /* READINESS POLL: token-carrying links (admin login links,
       email magic links) write auth to localStorage AFTER page
       load, and the subscription id can arrive via SPA
       navigation — so wait for both, whenever they appear,
       then initialize exactly once. */
    var booted = false;
    var bootTimer = setInterval(function () {
      if (booted) return;
      state.auth = getAuth();
      state.subId = getSubscriptionId();
      if (!state.auth || !state.subId) return;
      booted = true;
      clearInterval(bootTimer);
      initPortal();
    }, 200);
  }

  function initPortal() {
    injectStyles();

    Promise.all([
      fetchProducts(),
      fetchCollectionSet(SWAP_COLLECTION_HANDLES, null).then(function (list) {
        state.swapProducts = list;
      }),
      fetchCollectionSet(ADDON_COLLECTION_HANDLES, state.onetimeOnlyIds).then(function (list) {
        state.addonProducts = list;
      })
    ])
      .then(function () {
        injectAll();
        relabelOrderNow();

        var root = document.getElementById('bundleapp-subscriptions-root');
        if (root) {
          var pending = false;
          new MutationObserver(function () {
            if (pending) return;
            pending = true;
            setTimeout(function () {
              pending = false;
              injectAll();
              relabelOrderNow();
            }, 120);
          }).observe(root, { childList: true, subtree: true });
        }

        var tries = 0;
        var t = setInterval(function () {
          tries++;
          injectAll();
          if (state.lines.length || tries > 66) {
            clearInterval(t);
            if (!state.lines.length) {
              console.warn(DEBUG_TAG,
                'no subscription lines found \u2014 native flow unaffected');
            }
          }
        }, 300);
      })
      .catch(function (err) {
        console.error(DEBUG_TAG, 'init failed, native flow unaffected:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();